import React from 'react';
import type { TimeEntry, Expense, LineItemOverride } from '../types/billing.types';
import { EXPENSE_CATEGORY_LABELS } from '../types/billing.types';

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

interface BillableItemRowProps {
  item: TimeEntry | Expense;
  variant: 'time-entry' | 'expense';
  isSelected: boolean;
  onToggle: (id: string) => void;
  override: LineItemOverride | null;
  onOverrideChange: (id: string, update: Partial<LineItemOverride> | null) => void;
}

const BillableItemRow: React.FC<BillableItemRowProps> = ({
  item,
  variant,
  isSelected,
  onToggle,
  override,
  onOverrideChange,
}) => {
  const id = variant === 'time-entry'
    ? (item as TimeEntry).time_entry_id
    : (item as Expense).expense_id;

  const isTimeEntry = variant === 'time-entry';
  const te = isTimeEntry ? (item as TimeEntry) : null;
  const exp = !isTimeEntry ? (item as Expense) : null;

  const origQty = isTimeEntry ? Number(te!.duration) : Number(exp!.quantity);
  const origRate = isTimeEntry ? Number(te!.rate || 0) : Number(exp!.unit_cost);
  const origAmount = isTimeEntry ? Number(te!.amount) : Number(exp!.total_amount);
  const date = isTimeEntry ? te!.entry_date : exp!.expense_date;

  const billedQty = override?.billed_quantity ?? origQty;
  const billedRate = override?.billed_rate ?? origRate;
  const adjAmount = billedQty * billedRate;
  const hasChanges = override != null;

  const handleQtyChange = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    if (num === origQty && (override?.billed_rate == null || override.billed_rate === origRate) && !override?.adjustment_reason) {
      onOverrideChange(id, null);
    } else {
      onOverrideChange(id, { billed_quantity: num });
    }
  };

  const handleRateChange = (val: string) => {
    const num = parseFloat(val);
    if (isNaN(num)) return;
    if (num === origRate && (override?.billed_quantity == null || override.billed_quantity === origQty) && !override?.adjustment_reason) {
      onOverrideChange(id, null);
    } else {
      onOverrideChange(id, { billed_rate: num });
    }
  };

  const handleReasonChange = (val: string) => {
    if (!val && (override?.billed_quantity == null || override.billed_quantity === origQty) && (override?.billed_rate == null || override.billed_rate === origRate)) {
      onOverrideChange(id, null);
    } else {
      onOverrideChange(id, { adjustment_reason: val || undefined });
    }
  };

  const inputClasses = `w-full px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:ring-1 focus:ring-lex-accent focus:border-lex-accent disabled:opacity-40 disabled:cursor-not-allowed`;

  return (
    <tr className={`text-xs transition-colors ${hasChanges && isSelected ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''} ${!isSelected ? 'opacity-50' : ''}`}>
      <td className="px-2 py-1.5">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(id)}
          className="rounded border-gray-300 dark:border-gray-600"
        />
      </td>
      <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {formatDate(date)}
      </td>
      <td className="px-2 py-1.5 text-gray-900 dark:text-white max-w-[180px] truncate" title={item.description}>
        {item.description}
      </td>
      {!isTimeEntry && (
        <td className="px-2 py-1.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {EXPENSE_CATEGORY_LABELS[(item as Expense).expense_category] || (item as Expense).expense_category}
        </td>
      )}
      <td className="px-2 py-1.5 text-right text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
        {hasChanges ? (
          <span className="line-through">{isTimeEntry ? origQty.toFixed(1) : origQty}</span>
        ) : (
          isTimeEntry ? origQty.toFixed(1) : origQty
        )}
      </td>
      <td className="px-2 py-1.5 w-20">
        <input
          type="number"
          step={isTimeEntry ? '0.1' : '1'}
          min="0"
          value={billedQty}
          onChange={(e) => handleQtyChange(e.target.value)}
          disabled={!isSelected}
          className={`${inputClasses} text-right`}
        />
      </td>
      <td className="px-2 py-1.5 text-right text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
        {hasChanges && override?.billed_rate != null && override.billed_rate !== origRate ? (
          <span className="line-through">{formatCurrency(origRate)}</span>
        ) : (
          formatCurrency(origRate)
        )}
      </td>
      <td className="px-2 py-1.5 w-24">
        <input
          type="number"
          step="0.01"
          min="0"
          value={billedRate}
          onChange={(e) => handleRateChange(e.target.value)}
          disabled={!isSelected}
          className={`${inputClasses} text-right`}
        />
      </td>
      <td className="px-2 py-1.5 text-right font-medium tabular-nums whitespace-nowrap">
        <span className={hasChanges && adjAmount !== origAmount ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}>
          {formatCurrency(adjAmount)}
        </span>
      </td>
      <td className="px-2 py-1.5 w-32">
        <input
          type="text"
          placeholder="Reason..."
          value={override?.adjustment_reason || ''}
          onChange={(e) => handleReasonChange(e.target.value)}
          disabled={!isSelected || !hasChanges}
          className={inputClasses}
        />
      </td>
    </tr>
  );
};

export default BillableItemRow;
